import axios from 'axios';

export interface EnhancedBlog {
  id: string;
  title: string;
  slug: string;
  content: string;
  excerpt: string;
  featuredImage: string;
  authorName: string;
  authorAvatar: string;
  authorBio: string;
  category: string;
  tags: string[];
  status: 'draft' | 'published' | 'archived';
  publishedAt: string;
  readTime: number;
  views: number;
  likes: number;
  metaTitle: string;
  metaDescription: string;
  keywords: string[];
}

export interface BlogResponse {
  blogs: EnhancedBlog[];
  total: number;
  pages: number;
  currentPage: number;
}

interface CacheEntry<T> {
  data: T;
  timestamp: number;
  etag?: string;
  lastModified?: string;
}

interface CacheStorage {
  [key: string]: CacheEntry<any>;
}

class EnhancedBlogService {
  private baseURL = '/api/enhanced-blog';
  private cache: CacheStorage = {};
  private readonly CACHE_DURATION = 5 * 60 * 1000; // 5 minutes
  private readonly MAX_CACHE_SIZE = 100; // Maximum number of cached items

  private getCacheKey(endpoint: string, params?: string): string {
    return `${endpoint}${params ? `?${params}` : ''}`;
  }

  private isCacheValid(entry: CacheEntry<any>): boolean {
    return Date.now() - entry.timestamp < this.CACHE_DURATION;
  }

  private cleanupCache(): void {
    const keys = Object.keys(this.cache);
    if (keys.length > this.MAX_CACHE_SIZE) {
      // Remove oldest entries
      const sortedKeys = keys.sort((a, b) => 
        this.cache[a].timestamp - this.cache[b].timestamp
      );
      const keysToRemove = sortedKeys.slice(0, keys.length - this.MAX_CACHE_SIZE);
      keysToRemove.forEach(key => delete this.cache[key]);
    }
  }

  private async makeCachedRequest<T>(
    endpoint: string, 
    params?: string, 
    useCache: boolean = true
  ): Promise<T> {
    const cacheKey = this.getCacheKey(endpoint, params);
    const cachedEntry = this.cache[cacheKey];

    // If we have valid cached data and caching is enabled, return it
    if (useCache && cachedEntry && this.isCacheValid(cachedEntry)) {
      return cachedEntry.data;
    }

    try {
      // Prepare headers for conditional requests
      const headers: Record<string, string> = {};
      if (cachedEntry?.etag) {
        headers['If-None-Match'] = cachedEntry.etag;
      }
      if (cachedEntry?.lastModified) {
        headers['If-Modified-Since'] = cachedEntry.lastModified;
      }

      const response = await axios.get(`${this.baseURL}${endpoint}${params ? `?${params}` : ''}`, {
        headers
      });

      // If content hasn't changed (304 Not Modified), return cached data
      if (response.status === 304 && cachedEntry) {
        // Update timestamp to extend cache validity
        cachedEntry.timestamp = Date.now();
        return cachedEntry.data;
      }

      // Extract ETag and Last-Modified from response headers
      const etag = response.headers.etag;
      const lastModified = response.headers['last-modified'];

      // Cache the new data
      this.cache[cacheKey] = {
        data: response.data,
        timestamp: Date.now(),
        etag,
        lastModified
      };

      this.cleanupCache();
      return response.data;

    } catch (error: any) {
      // If it's a 304 Not Modified error, return cached data
      if (error.response?.status === 304 && cachedEntry) {
        cachedEntry.timestamp = Date.now();
        return cachedEntry.data;
      }
      
      // If we have stale cached data, return it as fallback
      if (cachedEntry) {
        console.warn('Using stale cache due to API error:', error.message);
        return cachedEntry.data;
      }
      
      throw error;
    }
  }

  async getBlogs(page = 1, limit = 10, category?: string, search?: string): Promise<BlogResponse> {
    const params = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString()
    });
    
    if (category) params.append('category', category);
    if (search) params.append('search', search);
    
    return this.makeCachedRequest<BlogResponse>('', params.toString());
  }

  async getBlogBySlug(slug: string): Promise<EnhancedBlog> {
    try {
      console.log('🔍 Fetching blog by slug:', slug);
      const response = await this.makeCachedRequest<{ success: boolean; blog: EnhancedBlog }>(`/${slug}`);
      
      console.log('📡 API Response:', response);
      
      if (response && response.success && response.blog) {
        console.log('✅ Blog found:', response.blog.title);
        return response.blog;
      } else {
        console.error('❌ Invalid response structure:', response);
        throw new Error('Invalid response structure from API');
      }
    } catch (error: any) {
      console.error('❌ Error fetching blog by slug:', error);
      if (error.response?.status === 404) {
        throw new Error('Blog not found');
      }
      throw error;
    }
  }

  /**
   * Create a new blog post with image upload
   */
  async createBlog(formData: FormData): Promise<any> {
    try {
      const response = await axios.post(`${this.baseURL}/create`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      
      // Invalidate cache after creating new content
      this.invalidateCache('*');
      
      return response.data;
    } catch (error) {
      console.error('Error creating blog:', error);
      throw error;
    }
  }

  /**
   * Update an existing blog post
   */
  async updateBlog(slug: string, formData: FormData): Promise<any> {
    try {
      const response = await axios.put(`${this.baseURL}/${slug}`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      
      // Invalidate cache for this specific blog
      this.invalidateCache(`*${slug}*`);
      
      return response.data;
    } catch (error) {
      console.error('Error updating blog:', error);
      throw error;
    }
  }

  /**
   * Delete a blog post
   */
  async deleteBlog(slug: string): Promise<any> {
    try {
      const response = await axios.delete(`${this.baseURL}/${slug}`);
      
      // Invalidate cache for this specific blog
      this.invalidateCache(`*${slug}*`);
      
      return response.data;
    } catch (error) {
      console.error('Error deleting blog:', error);
      throw error;
    }
  }

  /**
   * Get user's blogs
   */
  async getUserBlogs(): Promise<any> {
    try {
      const response = await axios.get(`${this.baseURL}/user/blogs`);
      return response.data;
    } catch (error) {
      console.error('Error fetching user blogs:', error);
      throw error;
    }
  }

  /**
   * Update blog status (publish/unpublish)
   */
  async updateBlogStatus(blogId: number, status: 'draft' | 'published'): Promise<any> {
    try {
      const response = await axios.patch(`${this.baseURL}/${blogId}/status`, { status });
      this.invalidateCache('*');
      return response.data;
    } catch (error) {
      console.error('Error updating blog status:', error);
      throw error;
    }
  }

  /**
   * Get user's case studies
   */
  async getUserCaseStudies(): Promise<any> {
    try {
      const response = await axios.get(`${this.baseURL}/user/case-studies`);
      return response.data;
    } catch (error) {
      console.error('Error fetching user case studies:', error);
      throw error;
    }
  }

  /**
   * Update case study status (publish/unpublish)
   */
  async updateCaseStudyStatus(caseStudyId: number, status: 'draft' | 'published'): Promise<any> {
    try {
      const response = await axios.patch(`${this.baseURL}/case-studies/${caseStudyId}/status`, { status });
      this.invalidateCache('*');
      return response.data;
    } catch (error) {
      console.error('Error updating case study status:', error);
      throw error;
    }
  }

  /**
   * Delete a case study
   */
  async deleteCaseStudy(caseStudyId: number): Promise<any> {
    try {
      const response = await axios.delete(`${this.baseURL}/case-studies/${caseStudyId}`);
      this.invalidateCache('*');
      return response.data;
    } catch (error) {
      console.error('Error deleting case study:', error);
      throw error;
    }
  }

  // Invalidate specific cache entries
  invalidateCache(pattern?: string): void {
    if (pattern) {
      // Invalidate cache entries matching the pattern
      Object.keys(this.cache).forEach(key => {
        if (key.includes(pattern)) {
          delete this.cache[key];
        }
      });
    } else {
      // Invalidate all cache
      this.cache = {};
    }
  }

  // Force refresh specific content
  async refreshBlog(slug: string): Promise<EnhancedBlog> {
    const cacheKey = this.getCacheKey(`/${slug}`);
    delete this.cache[cacheKey];
    return this.getBlogBySlug(slug);
  }

  // Force refresh blog list
  async refreshBlogs(page = 1, limit = 10, category?: string, search?: string): Promise<BlogResponse> {
    const params = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString()
    });
    
    if (category) params.append('category', category);
    if (search) params.append('search', search);
    
    const cacheKey = this.getCacheKey('', params.toString());
    delete this.cache[cacheKey];
    
    return this.getBlogs(page, limit, category, search);
  }

  // Get cache statistics
  getCacheStats(): { size: number; keys: string[]; oldest: number; newest: number } {
    const keys = Object.keys(this.cache);
    const timestamps = keys.map(key => this.cache[key].timestamp);
    
    return {
      size: keys.length,
      keys,
      oldest: Math.min(...timestamps),
      newest: Math.max(...timestamps)
    };
  }

  // Clear expired cache entries
  clearExpiredCache(): void {
    Object.keys(this.cache).forEach(key => {
      if (!this.isCacheValid(this.cache[key])) {
        delete this.cache[key];
      }
    });
  }
}

export default new EnhancedBlogService();
